import { useState } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus, RefreshCw, Star } from "lucide-react";

import { Button } from "../../components/ui/button";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { FilterBar, FilterField, PAGE_SIZE_OPTIONS, PagePager, inputCls, labelCls, orDash } from "../forex/parts";
import { Drawer, TabStrip, useTab } from "../forex-admin/parts";
import { STATEMENT_LABEL, post, useLedger, useLedgerCan, useLookups } from "./api";

const TABS = [
  { value: "years", label: "Fiscal Years" },
  { value: "currencies", label: "Currencies" },
  { value: "cost-centers", label: "Cost Centers" },
  { value: "headings", label: "Report Headings" },
  { value: "ledgers", label: "Ledgers" },
  { value: "accounts", label: "Chart of Accounts" },
];

const ACTIVE_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];
const FP = "financial_position";
const PL = "profit_and_loss";

const day = (iso) => (iso ? iso.slice(0, 10) : "");
const mono = (v) => <span className="font-mono">{orDash(v)}</span>;
const withAll = (options) => [{ value: "", label: "All" }, ...options];
const groupOptions = (lk) => (lk.options?.groups ?? []).map((g) => ({ value: String(g.id), label: `${g.id} · ${g.name}` }));
const reportOptions = (lk) => (lk.options?.reports ?? []).map((r) => ({ value: r, label: STATEMENT_LABEL[r] ?? r }));
const groupName = (lk, id) => lk.options?.groups.find((g) => g.id === id)?.name ?? id;
const tag = (text, tone = "border-border/70 text-muted-foreground") => (
  <span className={`inline-flex rounded-full border px-1.5 py-px text-[10px] font-medium ${tone}`}>{text}</span>
);
const BASE_TAG = tag("Base", "border-ginox-green/40 bg-ginox-green/10 text-ginox-green");

// `fields` are exactly what the backend accepts for the resource (unknown keys
// are a 400). createOnly = immutable after create; nullable = edit may clear
// it; show(form, record, editing) hides a field (and keeps it out of the body).
const RESOURCES = {
  currencies: {
    title: "Currencies",
    blurb: "Codes are assigned in order. One active currency is the base: every voucher total is kept in it and its rate is 1.",
    fields: [
      { name: "name", label: "Name", placeholder: "EUR" },
      {
        name: "isBase",
        label: "Base currency",
        type: "boolean",
        hint: "Rates of the other currencies are re-fetched against it.",
        show: (form, record, editing) => !(editing && record.isBase),
      },
      {
        name: "rate",
        label: "Rate (1 unit in the base currency)",
        optional: true,
        placeholder: "Leave blank to fetch the live market rate",
        show: (form) => !form.isBase,
      },
    ],
    columns: [
      ["Code", (r) => mono(r.code)],
      [
        "Name",
        (r) => (
          <span className="inline-flex items-center gap-2">
            {r.name}
            {r.isBase && BASE_TAG}
          </span>
        ),
      ],
      ["Rate to base", (r) => mono(r.isBase ? "1" : r.rate), "right"],
    ],
  },
  "cost-centers": {
    title: "Cost Centers",
    fields: [{ name: "name", label: "Name" }],
    columns: [
      ["Code", (r) => mono(r.code)],
      ["Name", (r) => r.name],
    ],
  },
  headings: {
    title: "Report Headings",
    blurb: "Financial Position headings take the debit or credit side of an account; Profit & Loss headings take the whole account.",
    filters: [{ name: "report", label: "Report", options: (lk) => withAll(reportOptions(lk)) }],
    fields: [
      { name: "name", label: "Name" },
      { name: "report", label: "Report", options: reportOptions },
      { name: "position", label: "Position", type: "number" },
    ],
    columns: [
      ["Report", (r) => STATEMENT_LABEL[r.report] ?? r.report],
      ["Position", (r) => mono(r.position), "right"],
      ["Name", (r) => r.name],
    ],
  },
  ledgers: {
    title: "Ledgers",
    blurb: "Optional 3-digit sub-groups (101, 102…) that accounts can sit under; accounts may also sit directly under a group.",
    filters: [{ name: "group", label: "Group", options: (lk) => withAll(groupOptions(lk)) }],
    fields: [
      { name: "group", label: "Group", type: "number", createOnly: true, options: groupOptions },
      { name: "name", label: "Name" },
    ],
    columns: [
      ["Code", (r) => mono(r.code)],
      ["Group", (r, lk) => groupName(lk, r.group)],
      ["Name", (r) => r.name],
    ],
  },
};

// Only fields the backend allows; numbers as numbers, booleans as booleans.
// On edit a cleared nullable field is sent as null, and `active` always goes.
function bodyOf(fields, form, editing, record) {
  const body = {};
  for (const f of fields) {
    if (editing && f.createOnly) continue;
    if (f.show && !f.show(form, record, editing)) continue;
    const v = form[f.name];
    if (f.type === "boolean") {
      body[f.name] = Boolean(v);
      continue;
    }
    if (v === "" || v == null) {
      if (editing && f.nullable) body[f.name] = null;
      continue;
    }
    body[f.name] = f.type === "number" ? Number(v) : v;
  }
  if (editing) body.active = form.active;
  return body;
}

function useSaved() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["trust-ledger"] });
}

function SaveButton({ saving, children = "Save", variant = "default" }) {
  return (
    <Button type="submit" variant={variant} disabled={saving} className="w-full">
      {saving ? "Saving…" : children}
    </Button>
  );
}

// Staging can take 30s+ on a write, so the button stays disabled until it answers.
function useSubmit(run, done) {
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      done(await run());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return [saving, submit];
}

function Field({ label, required = true, hint, children }) {
  return (
    <div>
      <label className={labelCls}>
        {label}
        {required ? "" : " (optional)"}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Check({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

function RecordForm({ resource, record, lk, onDone }) {
  const { fields } = RESOURCES[resource];
  const editing = Boolean(record._id);
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(
      fields.map((f) => [
        f.name,
        f.type === "boolean" ? Boolean(record[f.name]) : record[f.name] == null ? "" : String(record[f.name]),
      ]),
    ),
    active: record.active ?? true,
  }));
  const [saving, submit] = useSubmit(
    () => post(editing ? `/${resource}/${record._id}/edit` : `/${resource}`, bodyOf(fields, form, editing, record)),
    () => {
      toast.success(editing ? "Saved" : "Created");
      onDone();
    },
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      {fields
        .filter((f) => !(editing && f.createOnly) && (!f.show || f.show(form, record, editing)))
        .map((f) => {
          const options = f.options?.(lk, form);
          const set = (value) => setForm((s) => ({ ...s, [f.name]: value, ...(f.resets ? { [f.resets]: "" } : {}) }));
          const required = !f.optional && !f.nullable;
          if (f.type === "boolean") {
            return (
              <div key={f.name}>
                <Check checked={form[f.name]} onChange={set}>
                  {f.label}
                </Check>
                {f.hint && <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>}
              </div>
            );
          }
          return (
            <Field key={f.name} label={f.label} required={required} hint={f.hint}>
              {options ? (
                <select value={form[f.name]} onChange={(e) => set(e.target.value)} required={required} className={inputCls}>
                  <option value="">—</option>
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={form[f.name]}
                  onChange={(e) => set(e.target.value)}
                  required={required}
                  placeholder={f.placeholder}
                  className={inputCls}
                />
              )}
            </Field>
          );
        })}
      {editing && (
        <Check checked={form.active} onChange={(v) => setForm((s) => ({ ...s, active: v }))}>
          Active
        </Check>
      )}
      <SaveButton saving={saving} />
    </form>
  );
}

// `headerExtra` adds buttons beside Add; `rowExtra(record)` adds per-row actions.
function ResourceTab({ resource, headerExtra, rowExtra }) {
  const spec = RESOURCES[resource];
  const lk = useLookups();
  const can = useLedgerCan();
  const saved = useSaved();
  const empty = { search: "", active: "", pageSize: "50", ...Object.fromEntries((spec.filters ?? []).map((f) => [f.name, ""])) };
  const [draft, setDraft] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null); // {} = new, a record = edit

  const q = useLedger(`/${resource}`, { ...applied, search: applied.search.trim(), page });
  const canCreate = can("POST", `/${resource}`);
  const canEdit = can("POST", `/${resource}/:id/edit`);
  const actions = canEdit || rowExtra;
  const columns = [
    ...spec.columns.map(([label, , align]) => ({ label, align })),
    { label: "Status" },
    { label: "Created by" },
    { label: "Updated" },
    ...(actions ? [{ label: "" }] : []),
  ];

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium">{spec.title}</h2>
          {spec.blurb && <p className="text-xs text-muted-foreground">{spec.blurb}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {headerExtra}
          {canCreate && (
            <Button size="sm" variant="default" onClick={() => setOpen({})}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          )}
        </div>
      </div>
      <FilterBar
        onApply={() => {
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(empty);
          setApplied(empty);
          setPage(1);
        }}
      >
        <FilterField label="Search" name="search" draft={draft} setDraft={setDraft} placeholder="Name or code" />
        {(spec.filters ?? []).map((f) => (
          <FilterField key={f.name} label={f.label} name={f.name} options={f.options(lk)} draft={draft} setDraft={setDraft} />
        ))}
        <FilterField label="Status" name="active" options={ACTIVE_OPTIONS} draft={draft} setDraft={setDraft} />
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={columns}
            isLoading={q.isLoading}
            rows={q.data?.data}
            emptyText={`No ${spec.title.toLowerCase()} yet.`}
            renderRow={(r) => (
              <tr key={r._id} className="hover:bg-white/[0.02]">
                {spec.columns.map(([label, cell, align]) => (
                  <Td key={label} align={align}>
                    {cell(r, lk)}
                  </Td>
                ))}
                <Td className={r.active ? "text-emerald-400" : "text-muted-foreground"}>
                  {r.active ? "Active" : "Inactive"}
                </Td>
                <Td className="text-xs">{r.createdBy}</Td>
                <Td className="whitespace-nowrap text-xs text-muted-foreground">
                  {formatDateTime(r.updatedAt)} · {r.updatedBy}
                </Td>
                {actions && (
                  <Td align="right" className="whitespace-nowrap">
                    <div className="flex justify-end gap-1.5">
                      {rowExtra?.(r)}
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => setOpen(r)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  </Td>
                )}
              </tr>
            )}
          />
          <PagePager data={q.data} onChange={setPage} label={spec.title.toLowerCase()} />
        </>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(null)}
        title={open?._id ? `Edit ${[open.code, open.name].filter(Boolean).join(" · ")}` : `New ${spec.title.replace(/s$/, "").toLowerCase()}`}
      >
        {open && (
          <RecordForm
            key={open._id ?? "new"}
            resource={resource}
            record={open}
            lk={lk}
            onDone={() => {
              saved();
              setOpen(null);
            }}
          />
        )}
      </Drawer>
    </section>
  );
}

// ---------------------------------------------------------------- currencies

// Side-by-side stored vs live market rates against the base, from the keyless
// feeds (open.er-api.com / ECB for fiat, Binance / Toobit for crypto).
function PreviewRates() {
  const [refresh, setRefresh] = useState(false);
  const q = useLedger("/currencies/preview-rates", { refresh: refresh ? "true" : "" });
  const d = q.data;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {d ? `Against ${d.baseCurrency.name}${d.fetchedAt ? ` · fetched ${formatDateTime(d.fetchedAt)}` : ""}` : "Loading…"}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={q.isFetching}
          onClick={() => (refresh ? q.refetch() : setRefresh(true))}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <QueueTable
          columns={[{ label: "Currency" }, { label: "Stored", align: "right" }, { label: "Live", align: "right" }, { label: "Source" }]}
          isLoading={q.isLoading}
          rows={d?.rates}
          emptyText="No currencies yet."
          renderRow={(r) => (
            <tr key={r._id}>
              <Td>
                <span className="inline-flex items-center gap-2">
                  {r.name}
                  {r.isBase && BASE_TAG}
                </span>
              </Td>
              <Td align="right" className="font-mono">
                {r.currentRate}
              </Td>
              <Td
                align="right"
                className={`font-mono ${r.matched && r.liveRate && r.liveRate !== r.currentRate ? "text-amber-400" : ""}`}
              >
                {r.matched ? orDash(r.liveRate) : <span className="text-red-400">not found</span>}
              </Td>
              <Td className="text-xs text-muted-foreground">{orDash(r.source)}</Td>
            </tr>
          )}
        />
      )}
    </div>
  );
}

function SetBaseForm({ currency, onDone }) {
  const [syncRates, setSyncRates] = useState(true);
  const [saving, submit] = useSubmit(
    () => post(`/currencies/${currency._id}/set-base`, { syncRates }),
    (r) => {
      toast.success(`${currency.name} is now the base currency · ${r.updatedRatesCount} rate(s) updated`);
      onDone();
    },
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {currency.name} becomes the base currency with a rate of 1, and every new voucher line in it must use rate 1. The
        current base currency stays active as an ordinary currency. Posted vouchers keep the totals they were saved with.
      </p>
      <Check checked={syncRates} onChange={setSyncRates}>
        Re-fetch live rates for the other currencies against {currency.name}
      </Check>
      <SaveButton saving={saving}>Set {currency.name} as base</SaveButton>
    </form>
  );
}

function CurrenciesTab() {
  const can = useLedgerCan();
  const saved = useSaved();
  const [preview, setPreview] = useState(false);
  const [baseFor, setBaseFor] = useState(null);
  const [syncing, sync] = useSubmit(
    () => post("/currencies/sync-rates", {}),
    (r) => {
      const skipped = r.skipped?.length ? `; not found on the feeds: ${r.skipped.map((s) => s.name).join(", ")}` : "";
      toast.success(`${r.updatedCount} rate(s) updated${skipped}`);
      saved();
    },
  );
  const canSetBase = can("POST", "/currencies/:id/set-base");

  return (
    <>
      <ResourceTab
        resource="currencies"
        headerExtra={
          <>
            {can("GET", "/currencies/preview-rates") && (
              <Button size="sm" variant="outline" onClick={() => setPreview(true)}>
                Live rates
              </Button>
            )}
            {can("POST", "/currencies/sync-rates") && (
              <Button size="sm" variant="outline" disabled={syncing} onClick={sync}>
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync rates"}
              </Button>
            )}
          </>
        }
        rowExtra={(r) =>
          canSetBase && r.active && !r.isBase ? (
            <Button size="sm" variant="outline" title="Make this the base currency" onClick={() => setBaseFor(r)}>
              <Star className="h-3.5 w-3.5" /> Set as base
            </Button>
          ) : null
        }
      />
      <Drawer open={preview} onClose={() => setPreview(false)} title="Live market rates">
        {preview && <PreviewRates />}
      </Drawer>
      <Drawer open={baseFor} onClose={() => setBaseFor(null)} title={baseFor ? `Set ${baseFor.name} as base currency` : ""}>
        {baseFor && (
          <SetBaseForm
            currency={baseFor}
            onDone={() => {
              saved();
              setBaseFor(null);
            }}
          />
        )}
      </Drawer>
    </>
  );
}

// -------------------------------------------------------------------- years

function DateFields({ fields, form, setForm }) {
  return fields.map(([name, label, type = "text"]) => (
    <div key={name}>
      <label className={labelCls}>{label}</label>
      <input
        type={type}
        required
        value={form[name]}
        onChange={(e) => setForm((s) => ({ ...s, [name]: e.target.value }))}
        className={inputCls}
      />
    </div>
  ));
}

// Only the first year is created by hand; every later one comes from closing
// the open year, which starts the next one the moment after it ends.
function YearForm({ year, onDone }) {
  const closing = Boolean(year);
  const [form, setForm] = useState(
    closing ? { nextTitle: "", nextEndDate: "" } : { title: "", startDate: "", endDate: "" },
  );
  const [saving, submit] = useSubmit(
    () => post(closing ? `/years/${year._id}/close` : "/years", form),
    () => {
      toast.success(closing ? "Year closed, next year opened" : "Fiscal year created");
      onDone();
    },
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      {closing ? (
        <>
          <p className="text-sm text-muted-foreground">
            {year.title} closes for good: no receipt can be posted, edited or deleted in it after this. The next year starts{" "}
            {day(new Date(new Date(year.endDate).getTime() + 1).toISOString())}.
          </p>
          <DateFields
            fields={[
              ["nextTitle", "Next year title"],
              ["nextEndDate", "Next year end date", "date"],
            ]}
            form={form}
            setForm={setForm}
          />
        </>
      ) : (
        <DateFields
          fields={[
            ["title", "Title"],
            ["startDate", "Start date", "date"],
            ["endDate", "End date", "date"],
          ]}
          form={form}
          setForm={setForm}
        />
      )}
      <SaveButton saving={saving}>{closing ? "Close year" : "Create"}</SaveButton>
    </form>
  );
}

// An open year may be retitled or re-dated, as long as the new range still
// holds every receipt in it and touches no other year.
function YearEditForm({ year, onDone }) {
  const [form, setForm] = useState({ title: year.title, startDate: day(year.startDate), endDate: day(year.endDate) });
  const [saving, submit] = useSubmit(
    () => post(`/years/${year._id}/edit`, form),
    () => {
      toast.success("Fiscal year updated");
      onDone();
    },
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Dates can only move while every receipt already posted in {year.title} still falls inside them. Revision {year.revision}.
      </p>
      <DateFields
        fields={[
          ["title", "Title"],
          ["startDate", "Start date", "date"],
          ["endDate", "End date", "date"],
        ]}
        form={form}
        setForm={setForm}
      />
      <SaveButton saving={saving} />
    </form>
  );
}

function YearsTab() {
  const can = useLedgerCan();
  const saved = useSaved();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null); // { mode: "create" | "edit" | "close", year? }
  const q = useLedger("/years", { page, pageSize: 50 });
  const canClose = can("POST", "/years/:id/close");
  const canEdit = can("POST", "/years/:id/edit");
  const canCreate = can("POST", "/years") && q.data?.totalRows === 0;
  const titles = { create: "Create first fiscal year", edit: `Edit ${open?.year?.title}`, close: `Close ${open?.year?.title}` };
  const done = () => {
    saved();
    setOpen(null);
  };

  return (
    <section className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium">Fiscal Years</h2>
        {canCreate && (
          <Button size="sm" variant="default" onClick={() => setOpen({ mode: "create" })}>
            <Plus className="h-3.5 w-3.5" /> Create first year
          </Button>
        )}
      </div>
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={[
              { label: "Title" },
              { label: "Start" },
              { label: "End" },
              { label: "Status" },
              { label: "Revision", align: "right" },
              { label: "Created by" },
              { label: "Updated" },
              ...(canClose || canEdit ? [{ label: "" }] : []),
            ]}
            isLoading={q.isLoading}
            rows={q.data?.data}
            emptyText="No fiscal year yet. Create the first one to start posting."
            renderRow={(y) => (
              <tr key={y._id} className="hover:bg-white/[0.02]">
                <Td className="font-medium">{y.title}</Td>
                <Td className="font-mono">{day(y.startDate)}</Td>
                <Td className="font-mono">{day(y.endDate)}</Td>
                <Td className={y.status === "open" ? "text-emerald-400" : "text-muted-foreground"}>{y.status}</Td>
                <Td align="right" className="font-mono">
                  {y.revision}
                </Td>
                <Td className="text-xs">{y.createdBy}</Td>
                <Td className="whitespace-nowrap text-xs text-muted-foreground">
                  {formatDateTime(y.updatedAt)} · {y.updatedBy}
                </Td>
                {(canClose || canEdit) && (
                  <Td align="right" className="whitespace-nowrap">
                    {y.status === "open" && (
                      <div className="flex justify-end gap-1.5">
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => setOpen({ mode: "edit", year: y })}>
                            Edit
                          </Button>
                        )}
                        {canClose && (
                          <Button size="sm" variant="outline" onClick={() => setOpen({ mode: "close", year: y })}>
                            Close year
                          </Button>
                        )}
                      </div>
                    )}
                  </Td>
                )}
              </tr>
            )}
          />
          <PagePager data={q.data} onChange={setPage} label="years" />
        </>
      )}
      <Drawer open={open} onClose={() => setOpen(null)} title={open ? titles[open.mode] : ""}>
        {open?.mode === "edit" && <YearEditForm key={open.year._id} year={open.year} onDone={done} />}
        {open?.mode === "close" && <YearForm key={open.year._id} year={open.year} onDone={done} />}
        {open?.mode === "create" && <YearForm key="new" year={null} onDone={done} />}
      </Drawer>
    </section>
  );
}

// ----------------------------------------------------------------- accounts

// Where a new account goes, as one choice: directly under a fixed group,
// under a ledger, or under a group account. Encoded "kind:id".
const placement = {
  encode: (node) =>
    node.nodeType === "group" ? `group:${node.id}` : node.nodeType === "ledger" ? `ledger:${node._id}` : `account:${node._id}`,
  decode: (value) => {
    const [kind, id] = value.split(":");
    return kind === "group" ? { group: Number(id) } : kind === "ledger" ? { ledger: id } : { parentId: id };
  },
  options: (lk) => ({
    groups: (lk.options?.groups ?? []).map((g) => ({ value: `group:${g.id}`, label: `${g.id} · ${g.name}`, group: g.id })),
    ledgers: lk.ledgers.filter((l) => l.active).map((l) => ({ value: `ledger:${l._id}`, label: `${l.code} · ${l.name}`, group: l.group })),
    accounts: lk.accounts
      .filter((a) => a.active && a.isGroup)
      .map((a) => ({ value: `account:${a._id}`, label: `${a.code} · ${a.name}`, group: a.group })),
  }),
};

const defaultStatement = (group) => (group ? (group <= 3 ? FP : PL) : "");

function AccountForm({ record, parent, lk, onDone }) {
  const editing = Boolean(record?._id);
  const places = placement.options(lk);
  const groupOf = (value) => [...places.groups, ...places.ledgers, ...places.accounts].find((p) => p.value === value)?.group ?? null;
  const [form, setForm] = useState(() =>
    editing
      ? {
          name: record.name,
          isGroup: Boolean(record.isGroup),
          accountType: record.accountType ?? "",
          currency: record.currency ?? "",
          statementType: record.statementType || defaultStatement(record.group),
          heading: record.heading ?? "",
          debitHeading: record.debitHeading ?? "",
          creditHeading: record.creditHeading ?? "",
          active: record.active ?? true,
        }
      : {
          placement: parent ?? "",
          name: "",
          isGroup: false,
          accountType: "",
          currency: "",
          statementType: defaultStatement(groupOf(parent ?? "")),
          heading: "",
          debitHeading: "",
          creditHeading: "",
        },
  );
  const set = (patch) => setForm((s) => ({ ...s, ...patch }));
  const group = editing ? record.group : groupOf(form.placement);

  const [saving, submit] = useSubmit(
    () => {
      const body = {
        name: form.name,
        isGroup: form.isGroup,
        accountType: form.accountType,
        currency: form.currency,
        statementType: form.statementType,
      };
      // Each statement type has its own heading fields; the backend clears the others.
      if (!form.isGroup) {
        if (form.statementType === FP) {
          body.debitHeading = form.debitHeading || null;
          body.creditHeading = form.creditHeading || null;
        } else body.heading = form.heading || null;
      }
      if (editing) body.active = form.active;
      else Object.assign(body, placement.decode(form.placement));
      return post(editing ? `/accounts/${record._id}/edit` : "/accounts", body);
    },
    (r) => {
      toast.success(editing ? "Saved" : `Account ${r.code} created`);
      onDone();
    },
  );

  const headings = (report) =>
    lk.headings.filter((h) => h.active && h.report === report).map((h) => ({ value: h._id, label: `${h.position}. ${h.name}` }));
  const select = (name, options, required = false) => (
    <select value={form[name]} onChange={(e) => set({ [name]: e.target.value })} required={required} className={inputCls}>
      <option value="">—</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
  const optgroup = (label, options) =>
    options.length > 0 && (
      <optgroup label={label}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </optgroup>
    );
  const accountTypes = lk.options?.accountTypes ?? [];
  const currencies = lk.currencies.filter((c) => c.active || c.name === form.currency).map((c) => ({ value: c.name, label: c.name }));

  return (
    <form onSubmit={submit} className="space-y-4">
      {editing ? (
        <p className="text-sm text-muted-foreground">
          Code <span className="font-mono">{record.code}</span> · {groupName(lk, record.group)}. An account cannot move once created.
        </p>
      ) : (
        <Field label="Place under" hint="The code is generated from the parent's code; the group follows the parent.">
          <select value={form.placement} required className={inputCls} onChange={(e) => {
            const value = e.target.value;
            set({ placement: value, statementType: defaultStatement(groupOf(value)), heading: "", debitHeading: "", creditHeading: "" });
          }}>
            <option value="">—</option>
            {optgroup("Directly under a group", places.groups)}
            {optgroup("Under a ledger", places.ledgers)}
            {optgroup("Under a group account", places.accounts)}
          </select>
        </Field>
      )}
      <Field label="Account name">
        <input value={form.name} required onChange={(e) => set({ name: e.target.value })} className={inputCls} />
      </Field>
      <div>
        <Check checked={form.isGroup} onChange={(v) => set({ isGroup: v })}>
          Group account
        </Check>
        <p className="mt-1 text-xs text-muted-foreground">
          Group accounts organise; only leaf accounts receive postings. {editing && "A group with children, or a leaf with postings, cannot switch."}
        </p>
      </div>
      {!form.isGroup && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Account type" required={false}>
              {select("accountType", [
                ...accountTypes.map((t) => ({ value: t, label: t })),
                ...(form.accountType && !accountTypes.includes(form.accountType) ? [{ value: form.accountType, label: form.accountType }] : []),
              ])}
            </Field>
            <Field label="Currency" required={false}>
              {select("currency", currencies)}
            </Field>
          </div>
          <Field label="Statement" hint={group ? `${groupName(lk, group)} accounts usually sit on the ${STATEMENT_LABEL[defaultStatement(group)]}.` : undefined}>
            <select
              value={form.statementType}
              required
              className={inputCls}
              onChange={(e) => set({ statementType: e.target.value, heading: "", debitHeading: "", creditHeading: "" })}
            >
              <option value="">—</option>
              {(lk.options?.statementTypes ?? [FP, PL]).map((s) => (
                <option key={s} value={s}>
                  {STATEMENT_LABEL[s] ?? s}
                </option>
              ))}
            </select>
          </Field>
          {form.statementType === FP && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Heading when balance is debit" required={false} hint="Assets / debtors side.">
                {select("debitHeading", headings(FP))}
              </Field>
              <Field label="Heading when balance is credit" required={false} hint="Liabilities / creditors side.">
                {select("creditHeading", headings(FP))}
              </Field>
            </div>
          )}
          {form.statementType === PL && (
            <Field label="Report heading" required={false}>
              {select("heading", headings(PL))}
            </Field>
          )}
        </>
      )}
      {editing && (
        <Check checked={form.active} onChange={(v) => set({ active: v })}>
          Active
        </Check>
      )}
      <SaveButton saving={saving} />
    </form>
  );
}

const kindOf = (n) => (n.nodeType === "group" ? "Group" : n.nodeType === "ledger" ? "Ledger" : n.isGroup ? "Group account" : "Account");

function TreeNode({ node, lk, canCreate, canEdit, onAdd, onEdit }) {
  const [expanded, setExpanded] = useState(true);
  const kids = node.children ?? [];
  const leaf = node.nodeType === "account" && !node.isGroup;
  const inactive = node.nodeType !== "group" && node.active === false;
  const head = (id) => lk.heading[id]?.name;
  const headingText = !leaf
    ? null
    : node.statementType === PL
      ? head(node.heading)
      : [head(node.debitHeading) && `Dr → ${head(node.debitHeading)}`, head(node.creditHeading) && `Cr → ${head(node.creditHeading)}`]
          .filter(Boolean)
          .join(" · ");
  return (
    <>
      <div
        className={`flex flex-wrap items-center gap-2 border-b border-border/30 py-1.5 pr-2 hover:bg-white/[0.02] ${inactive ? "opacity-60" : ""}`}
        style={{ paddingLeft: 8 + node.level * 22 }}
      >
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          disabled={!kids.length}
          className="w-4 text-muted-foreground disabled:opacity-0"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="w-20 font-mono text-xs">{node.code}</span>
        <span className={`text-sm ${leaf ? "" : "font-medium"} ${inactive ? "line-through" : ""}`}>{node.name}</span>
        {tag(leaf && node.accountType ? node.accountType : kindOf(node))}
        {leaf && node.currency && tag(node.currency)}
        {leaf && node.statementType && tag(STATEMENT_LABEL[node.statementType] ?? node.statementType)}
        {inactive && tag("Inactive", "border-red-500/30 text-red-400")}
        {headingText && <span className="text-[11px] text-muted-foreground">{headingText}</span>}
        {!leaf && node.leafCount != null && (
          <span className="text-[11px] text-muted-foreground">
            {node.leafCount} postable
          </span>
        )}
        <span className="ml-auto flex gap-1.5">
          {!leaf && canCreate && !inactive && (
            <Button size="sm" variant="ghost" onClick={() => onAdd(node)}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          )}
          {node.nodeType === "account" && canEdit && (
            <Button size="sm" variant="outline" onClick={() => onEdit(node)}>
              Edit
            </Button>
          )}
        </span>
      </div>
      {expanded &&
        kids.map((k) => (
          <TreeNode key={k._id ?? k.id} node={k} lk={lk} canCreate={canCreate} canEdit={canEdit} onAdd={onAdd} onEdit={onEdit} />
        ))}
    </>
  );
}

const SHOW_OPTIONS = [
  { value: "", label: "Active only" },
  { value: "true", label: "Include inactive" },
];

function AccountsTab() {
  const lk = useLookups();
  const can = useLedgerCan();
  const saved = useSaved();
  const empty = { search: "", group: "", showDisabled: "" };
  const [draft, setDraft] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const [open, setOpen] = useState(null); // { parent } = new, { record } = edit
  const q = useLedger("/accounts/tree", { ...applied, search: applied.search.trim() });
  const canCreate = can("POST", "/accounts");
  const canEdit = can("POST", "/accounts/:id/edit");
  const counts = q.data?.counts;

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium">Chart of Accounts</h2>
          <p className="text-xs text-muted-foreground">
            Five fixed groups, then ledgers and group accounts as folders, then the leaf accounts vouchers post to.
          </p>
        </div>
        {canCreate && (
          <Button size="sm" variant="default" onClick={() => setOpen({ parent: "" })}>
            <Plus className="h-3.5 w-3.5" /> Add account
          </Button>
        )}
      </div>
      <FilterBar onApply={() => setApplied(draft)} onReset={() => { setDraft(empty); setApplied(empty); }}>
        <FilterField label="Search" name="search" draft={draft} setDraft={setDraft} placeholder="Name or code" />
        <FilterField label="Group" name="group" options={withAll(groupOptions(lk))} draft={draft} setDraft={setDraft} />
        <FilterField label="Show" name="showDisabled" options={SHOW_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {counts && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
          <span>{counts.all} accounts</span>
          {(lk.options?.groups ?? []).map((g) => (
            <span key={g.id}>
              {g.name}: <span className="font-mono text-foreground">{counts.byGroup?.[g.id] ?? 0}</span>
            </span>
          ))}
        </div>
      )}

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border/70">
          <div className="min-w-[760px]">
            {q.data.tree.map((g) => (
              <TreeNode
                key={g.id}
                node={g}
                lk={lk}
                canCreate={canCreate}
                canEdit={canEdit}
                onAdd={(n) => setOpen({ parent: placement.encode(n) })}
                onEdit={(n) => setOpen({ record: n })}
              />
            ))}
            {q.data.tree.every((g) => !g.children?.length) && (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground">No accounts match.</p>
            )}
          </div>
        </div>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(null)}
        title={open?.record ? `Edit ${open.record.code} · ${open.record.name}` : "New account"}
      >
        {open && (
          <AccountForm
            key={open.record?._id ?? open.parent ?? "new"}
            record={open.record}
            parent={open.parent}
            lk={lk}
            onDone={() => {
              saved();
              setOpen(null);
            }}
          />
        )}
      </Drawer>
    </section>
  );
}

export default function TrustLedgerSetup() {
  const [asked, setTab] = useTab("years");
  const tab = TABS.some((t) => t.value === asked) ? asked : "years";
  const qc = useQueryClient();
  return (
    <QueueLayout
      eyebrow="Trust Ledger"
      title="Ledger"
      accent="setup"
      description="Fiscal years, currencies, cost centers, report headings and the chart of accounts that vouchers post to."
      onRefresh={() => qc.invalidateQueries({ queryKey: ["trust-ledger"] })}
    >
      <TabStrip tabs={TABS} value={tab} onChange={setTab} />
      {tab === "years" && <YearsTab />}
      {tab === "currencies" && <CurrenciesTab />}
      {tab === "accounts" && <AccountsTab />}
      {["cost-centers", "headings", "ledgers"].includes(tab) && <ResourceTab key={tab} resource={tab} />}
    </QueueLayout>
  );
}
